import { sameDistrict } from './districts.js';

/**
 * Where the vehicle will be registered (owner, 16 Sep 2026: "a selector for
 * region of RC and selections are Lucknow, sitapur, barabanki, unnao,
 * lakhimpur, shahjahanpur, raibariely, hardoi, amethi").
 *
 * The RTO the registration certificate is applied for, which is a fact about
 * the SALE and not about the customer — a buyer from one district routinely
 * registers where the counter is. Nine offices, in the owner's own order; it
 * is a picker and not free text, so the office never has to fold "Rae Bareli",
 * "Raebareli" and "raibariely" into one report row.
 *
 * Both the sale form and the server read this one list.
 */
export const RC_REGIONS = [
  'Lucknow',
  'Sitapur',
  'Barabanki',
  'Unnao',
  'Lakhimpur',
  'Shahjahanpur',
  'Raebareli',
  'Hardoi',
  'Amethi',
  // Owner, 19 Sep 2026: "add sultanpur as well, price of rto is 4000".
  'Sultanpur',
  // Owner, 24 Sep 2026: "Also add one more rto kanpur price 2800".
  'Kanpur',
] as const;
export type RcRegion = (typeof RC_REGIONS)[number];

/**
 * The customer gets the RC made themselves (owner, 24 Sep 2026: "Add a option
 * in sale RC BY OWN in this person can select this and no rto charge will be
 * added and move ahead in the form"). A choice beside the offices, not one of
 * them: it answers "where will the RC be made?" with "not by us", so no RTO is
 * charged and the billing desk knows not to apply for it. Stored in the same
 * `Vehicle.rcRegion` field as the offices, spelled as the owner wrote it.
 */
export const RC_BY_OWN = 'RC BY OWN' as const;
export type RcByOwn = typeof RC_BY_OWN;

/** Every answer the sale form takes to "where will the RC be made?". */
export const RC_CHOICES = [...RC_REGIONS, RC_BY_OWN] as const;
export type RcChoice = (typeof RC_CHOICES)[number];

export function isRcByOwn(value: string | null | undefined): boolean {
  return (value ?? '').trim().toUpperCase() === RC_BY_OWN;
}

/**
 * Whether a sale needs the buyer's rent agreement: the RC is to be made in a
 * district the buyer's Aadhaar is not from.
 *
 * Owner, 6 Oct 2026: "aadhar is from barabanki and rto also is from barabanki
 * why is it asking for rent agreement". Until then the Aadhaar was held
 * against the COUNTER's district, so a Barabanki customer at a Lucknow counter
 * registering in Barabanki was asked for one. The RTO chosen on the form
 * decides now. With none chosen yet the counter's district stands in — the
 * rule as it was; "RC BY OWN" (the customer registers it) asks nothing; an
 * Aadhaar with no district read asks nothing either.
 */
export function rentAgreementNeeded(
  buyerDistrict: string | null | undefined,
  rcRegion: string | null | undefined,
  counterDistrict: string | null | undefined,
): boolean {
  if (!buyerDistrict) return false;
  if (isRcByOwn(rcRegion)) return false;
  return !sameDistrict(buyerDistrict, rcRegion?.trim() || counterDistrict);
}

/**
 * What a cash customer actually pays (owner, 16 Sep 2026: "the final amount
 * displayed if the person choose cash would be base price + RTO + insurance
 * price ... RTO is different for every region ... insurance price for cargo is
 * 3000 and L3 passanger 5000, L5 passanger is 6500 ... if the person choose own
 * insurance then only then base price will be shown").
 *
 * The base is the office's price list. The two heads on top are FIXED figures
 * — a picker of region and a class read off the vehicle — so the counter
 * never types money, and the office's figure is the same at every counter.
 *
 * Own insurance: the customer is making their own arrangements, so BOTH heads
 * come off and they pay the bare list price (owner, 16 Sep 2026, asked
 * directly: "own insurance means no rto as well"). The region is still picked
 * — it is still where the vehicle will be registered — it is simply not
 * charged for here.
 */
export const RTO_FEES: Record<RcRegion, number> = {
  Lucknow: 2500,
  Sitapur: 3600,
  Barabanki: 5000,
  Unnao: 3000,
  Lakhimpur: 4800,
  Shahjahanpur: 2250,
  Raebareli: 2500,
  Hardoi: 3000,
  Amethi: 2500,
  Sultanpur: 4000,
  Kanpur: 2800,
};

/** Cargo, or a passenger body of either class. */
export type VehicleClass = 'CARGO' | 'L3' | 'L5';

export const INSURANCE_FEES: Record<VehicleClass, number> = {
  CARGO: 3000,
  L3: 5000,
  L5: 6500,
};

export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  CARGO: 'Cargo',
  L3: 'L3 passenger',
  L5: 'L5 passenger',
};

/**
 * Which of the three a stock vehicle is, read off the model the sheet gave it.
 *
 * The catalogue knows bodies by order key, but stock carries free text —
 * "CITYCAB CARGO", "CITY CAB E-MAX", "CITY CAB SUPER". Cargo says so in its
 * name; E-Max and Pro are the L5 builds (the same split the order catalogue
 * makes); everything else the plant builds is an L3 passenger body, which is
 * also the safe default for a name nobody has seen before.
 */
export function vehicleClassFor(model: string | null | undefined, variant?: string | null): VehicleClass {
  const text = `${model ?? ''} ${variant ?? ''}`.toUpperCase();
  // TVS spells it KARGO ("TVS KING KARGO HD FSD CNG", 19 Oct 2026): a cargo body all the same.
  if (/\b[CK]ARGO\b|\bLOADER\b|\bDUST\s*CART\b/.test(text)) return 'CARGO';
  if (/E[\s-]*MAX|\bPRO\b/.test(text)) return 'L5';
  return 'L3';
}

/**
 * Financing (owner, 17 Sep 2026): "in finance section person will choose
 * financer from finance list for bajaj hinduja and perfect the insurance price
 * is 1000, 1500 and 2000 and if a person chooses any other financer than this
 * then price for insurance cargo - 4500, L3 passenger - 6800, L5 passenger -
 * 9000 so the total would be base price + insurance + RTO + HPA 1500rs fixed
 * so the person will fill loan amount and file charge that will be removed
 * from our total and it will show him the balance".
 *
 * Three financiers get the cheap policy; every other gets the dear one; and a
 * financed vehicle carries the hypothecation charge. The three figures are in
 * the same order as the class list — cargo, L3, L5 — which is how the two
 * tables are read together.
 */
export const HPA_FEE = 1500;

/**
 * The policy on a financed sale (owner, 17 Sep 2026, revised that evening:
 * "insurance price of bajaj, hinduja and perfect for cargo is 4500, L3 is
 * 6800 and L5 is 9000 and for other financer send me a list i will fill
 * against them"). So these three figures are the rule for the named three,
 * every other financier carries its own three set by the office in
 * Administration › Prices, and a financier the office has not filled in yet
 * is charged these until it does.
 */
export const FINANCE_INSURANCE_DEFAULT: Record<VehicleClass, number> = { CARGO: 4500, L3: 6800, L5: 9000 };

/** Bajaj, Hinduja and Perfect, however the counter spelt or picked them — the three on the default. */
export function isPreferredFinancier(name: string | null | undefined): boolean {
  return /BAJAJ|HINDUJA|PERFECT/i.test(name ?? '');
}

/** A financier's own policy figures, as the office set them. Any class missing falls back to the default. */
export type FinancierInsurance = Partial<Record<VehicleClass, number>>;

export interface SaleHeads {
  base: number;
  rto: number;
  insurance: number;
  /** The hypothecation charge — on a financed sale only. */
  hpa: number;
  /** A bigger battery than the variant is priced with (17 Sep 2026). */
  battery: number;
  total: number;
}

/** The sum the counter shows and the sale records. All figures in rupees. */
export function saleHeads(input: {
  base: number;
  /** An office, or RC_BY_OWN — the customer registers it themselves, no RTO (24 Sep 2026). */
  rcRegion: RcChoice | null | undefined;
  vehicleClass: VehicleClass;
  ownInsurance: boolean;
  /** Cash unless said otherwise — the sale form's two buttons. */
  payment?: 'CASH' | 'FINANCE';
  /** Which financier, for the record. Ignored on a cash sale. */
  financierName?: string | null;
  /** The financier's own policy figures, when the office has set them. */
  financierInsurance?: FinancierInsurance | null;
  /** The battery's price step, worked out by `batteryUpgradeFee` — less than nought for a smaller battery (22 Oct 2026). */
  batteryUpgrade?: number | null;
}): SaleHeads {
  const financed = input.payment === 'FINANCE';
  // A financed vehicle is insured through the financier — "own insurance" is
  // a cash-sale choice and does not apply here.
  const own = !financed && input.ownInsurance;
  const rto = own || !input.rcRegion || isRcByOwn(input.rcRegion) ? 0 : (RTO_FEES[input.rcRegion as RcRegion] ?? 0);
  const insurance = own
    ? 0
    : financed
      ? (input.financierInsurance?.[input.vehicleClass] ?? FINANCE_INSURANCE_DEFAULT[input.vehicleClass])
      : INSURANCE_FEES[input.vehicleClass];
  const hpa = financed ? HPA_FEE : 0;
  const battery = input.batteryUpgrade ?? 0;
  const total = Math.round((input.base + rto + insurance + hpa + battery) * 100) / 100;
  return { base: input.base, rto, insurance, hpa, battery, total };
}

/**
 * The battery ladder (owner, 17 Sep 2026: "if someone upgrades 105ah battery
 * to 140ah in the same varient then the price will be increase by 15k if
 * battery 140ah varient is upgraded to 150ah then price increase 10k and in
 * 150ah variant if battery is upgraded to 232 then price increase 50k").
 *
 * A variant is priced with a STANDARD battery, set by the office in Prices.
 * The vehicle's own battery is what the bay recorded on the dispatch form.
 * When the fitted one sits higher on the ladder, the steps between the two
 * are charged, one after the other: 105 → 150 is the 105 → 140 step plus the
 * 140 → 150 step. A battery off the ladder (132 Ah, 107 Ah, lead-acid) is not
 * an upgrade the owner has priced, so it is charged nothing.
 */
export const BATTERY_LADDER = [105, 140, 150, 232] as const;
export type BatteryAh = (typeof BATTERY_LADDER)[number];
export const BATTERY_STEP_FEES: Readonly<Record<string, number>> = { '105>140': 15000, '140>150': 10000, '150>232': 50000 };

/** "140 AH Lithium 36 M" → 140; "WITHOUT BATTERY" → null. */
export function batteryAhFrom(text: string | null | undefined): number | null {
  const raw = (text ?? '').toUpperCase();
  if (!raw || /WITHOUT/.test(raw)) return null;
  const match = /(\d{2,3})\s*AH\b/.exec(raw);
  return match ? Number(match[1]) : null;
}

/**
 * The rung of the ladder a battery belongs to — the nearest one, the lower on
 * a tie. The makers' capacities are not the owner's round figures: an E-MAX
 * ECO Li "comes with 105Ah" but is sent with a 132Ah pack, a 107Ah pack is the
 * 105 class, the lead-acid packs read 135 and 125. The owner prices the class,
 * not the label (18 Sep 2026: "same variant is sent by 132ah battery … it
 * should automatically show price according to battery").
 */
export function batteryRung(ah: number): BatteryAh {
  let best: BatteryAh = BATTERY_LADDER[0];
  for (const rung of BATTERY_LADDER) {
    if (Math.abs(rung - ah) < Math.abs(best - ah)) best = rung;
  }
  return best;
}

/**
 * The price step for the battery fitted against the standard one: the steps
 * between their classes, added for a bigger battery and — since the owner's
 * answer on swaps (22 Oct 2026: a smaller battery's price goes down "same
 * steps down") — taken off for a smaller one. Nought for the standard's own
 * class, and when either battery is not known.
 */
export function batteryUpgradeFee(standardAh: number | null | undefined, fittedAh: number | null | undefined): number {
  if (!standardAh || !fittedAh) return 0;
  const from = BATTERY_LADDER.indexOf(batteryRung(standardAh));
  const to = BATTERY_LADDER.indexOf(batteryRung(fittedAh));
  const step = (i: number) => BATTERY_STEP_FEES[`${BATTERY_LADDER[i]}>${BATTERY_LADDER[i + 1]}`] ?? 0;
  let fee = 0;
  for (let i = from; i < to; i += 1) fee += step(i);
  for (let i = to; i < from; i += 1) fee -= step(i);
  return fee;
}

/**
 * The billing chain's papers of a sale that was undone (16 Oct 2026). The
 * owner may undo a sale after the bill — "Add a option so that i can undo bill
 * uploaded sale as well" — and no sale file is ever deleted, so the bill, the
 * policy and the RTO receipt stay on the vehicle under a kind of their own.
 * Under their old kind they would still read as this vehicle's bill (the
 * counter's form would stay locked) and the next sale's upload would replace —
 * delete — them. None of these kinds can be uploaded or removed.
 */
export const UNDONE_PAPER_KINDS = {
  retail_bill: 'retail_bill_undone',
  insurance_doc: 'insurance_doc_undone',
  rto_receipt: 'rto_receipt_undone',
  // The office's sale on the bill's photo, undone by the owner (31 Oct 2026).
  bill_photo: 'bill_photo_undone',
} as const;
export type UndonePaperSource = keyof typeof UNDONE_PAPER_KINDS;
export type UndonePaperKind = (typeof UNDONE_PAPER_KINDS)[UndonePaperSource];

const UNDONE_KIND_SET: ReadonlySet<string> = new Set(Object.values(UNDONE_PAPER_KINDS));

/** A paper of an undone sale: kept, shown as undone, never counted, replaced or removed. */
export function isUndonePaper(kind: string): boolean {
  return UNDONE_KIND_SET.has(kind);
}

/*
 * A sold vehicle's paper the office replaced (owner, 30 Oct 2026: "give admin power to change
 * papers or update the missing paper"). Once sold the papers are the sale's evidence, so the one
 * replaced is kept under its own kind — shown as replaced, never counted as the paper itself.
 */
const REPLACED_SUFFIX = '_replaced';
export function replacedKindOf(kind: string): string {
  return `${kind}${REPLACED_SUFFIX}`;
}
export function isReplacedPaper(kind: string): boolean {
  return kind.endsWith(REPLACED_SUFFIX);
}
/** The kind a replaced paper was, for its label. */
export function replacedFrom(kind: string): string {
  return kind.slice(0, -REPLACED_SUFFIX.length);
}
